import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import QrCodeIcon from '@mui/icons-material/QrCode2';
import QRCode from 'react-qr-code';
import {
  RmetSubmissionDialog,
  durationBetween,
  promptScoreLabel,
  type RmetSubmission,
} from './RmetSubmissionDialog';

/**
 * RMET test sessions. These live in the same `upload-sessions` table as the
 * recording upload sessions, distinguished by `type: 'rmet'` — so this panel
 * filters the shared list rather than calling its own operations.
 */
interface RmetSession {
  id: string;
  label: string;
  lang: string;
  type: string;
  status: 'open' | 'closed';
  createdAt: string;
  createdBy?: string;
  /** Only present locally, right after creation — never returned by the API. */
  passcode?: string;
}

const rmetPageUrl = (sessionId: string): string => `${window.location.origin}/rmet/${sessionId}`;

const RMET_TOTAL = 36;

interface Props {
  apiFetch: (body: Record<string, unknown>) => Promise<Response>;
  isAdmin?: boolean;
}

export const TableRmet: React.FC<Props> = ({ apiFetch, isAdmin }) => {
  const { t, i18n } = useTranslation();

  const [sessions, setSessions] = React.useState<RmetSession[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [newLabel, setNewLabel] = React.useState('');
  const [newPasscode, setNewPasscode] = React.useState('');
  const [newLang, setNewLang] = React.useState(i18n.language ?? 'it');
  const [creating, setCreating] = React.useState(false);
  const [qrSession, setQrSession] = React.useState<RmetSession | null>(null);

  // Submissions are fetched per session the first time its row is expanded,
  // then kept so collapsing and re-expanding is instant.
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const [submissions, setSubmissions] = React.useState<Record<string, RmetSubmission[]>>({});
  const [loadingSubmissions, setLoadingSubmissions] = React.useState<Set<string>>(new Set());
  const [openSubmission, setOpenSubmission] = React.useState<RmetSubmission | null>(null);

  const loadSubmissions = React.useCallback(
    async (sessionId: string) => {
      setLoadingSubmissions((prev) => new Set(prev).add(sessionId));
      try {
        const res = await apiFetch({ operation: 'list_rmet_responses', sessionId });
        const json = await res.json();
        setSubmissions((prev) => ({
          ...prev,
          [sessionId]: (json['output'] as RmetSubmission[]) ?? [],
        }));
      } catch (err) {
        console.error('Failed to load RMET submissions', err);
      }
      setLoadingSubmissions((prev) => {
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
    },
    [apiFetch]
  );

  const toggleExpanded = (sessionId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
        if (!submissions[sessionId]) loadSubmissions(sessionId);
      }
      return next;
    });
  };

  const handleRescore = async (responseId: string) => {
    const res = await apiFetch({ operation: 'rescore_rmet_response', responseId });
    if (!res.ok) return;
    // Reflect 'pending' immediately, then refresh that session so the finished
    // score appears once the async job lands.
    const patch = (r: RmetSubmission) =>
      r.id === responseId ? { ...r, promptScoreStatus: 'pending' as const } : r;
    setSubmissions((prev) =>
      Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, v.map(patch)]))
    );
    setOpenSubmission((prev) => (prev && prev.id === responseId ? patch(prev) : prev));
  };

  const loadSessions = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch({ operation: 'list_upload_sessions' });
      const json = await res.json();
      const all = (json['output'] as RmetSession[]) ?? [];
      setSessions(all.filter((s) => s.type === 'rmet'));
    } catch (err) {
      console.error('Failed to load RMET sessions', err);
    }
    setLoading(false);
  }, [apiFetch]);

  React.useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleCreate = async () => {
    if (!newLabel.trim() || !newPasscode.trim()) return;
    setCreating(true);
    try {
      const res = await apiFetch({
        operation: 'create_upload_session',
        sessionLabel: newLabel.trim(),
        passcode: newPasscode.trim(),
        lang: newLang,
        sessionType: 'rmet',
      });
      const json = await res.json();
      const passcode = newPasscode.trim();
      // Keep the passcode in local state so it can be shown alongside the QR
      // code once — the API never returns it again.
      setSessions((prev) => [{ ...(json['output'] as RmetSession), passcode }, ...prev]);
      setCreateOpen(false);
      setNewLabel('');
      setNewPasscode('');
    } catch (err) {
      console.error('Failed to create RMET session', err);
    }
    setCreating(false);
  };

  const handleClose = async (id: string) => {
    await apiFetch({ operation: 'close_upload_session', sessionId: id });
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, status: 'closed' } : s)));
  };

  const handleDelete = async (id: string) => {
    await apiFetch({ operation: 'delete_upload_session', sessionId: id });
    setSessions((prev) => prev.filter((s) => s.id !== id));
  };

  return (
    <>
      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('rmet.admin_create')}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label={t('rmet.admin_label')}
              value={newLabel}
              size="small"
              fullWidth
              onChange={(e) => setNewLabel(e.target.value)}
            />
            <TextField
              label={t('rmet.admin_passcode')}
              value={newPasscode}
              size="small"
              fullWidth
              onChange={(e) => setNewPasscode(e.target.value)}
            />
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2">{t('nav.language')}:</Typography>
              <Button
                size="small"
                variant={newLang === 'it' ? 'contained' : 'outlined'}
                onClick={() => setNewLang('it')}
              >
                IT
              </Button>
              <Button
                size="small"
                variant={newLang === 'en' ? 'contained' : 'outlined'}
                onClick={() => setNewLang('en')}
              >
                EN
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!newLabel.trim() || !newPasscode.trim()}
            loading={creating}
          >
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR dialog */}
      <Dialog open={qrSession !== null} onClose={() => setQrSession(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{qrSession?.label}</DialogTitle>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          flex={1}
          p={4}
          gap={3}
        >
          {qrSession && (
            <>
              <QRCode value={rmetPageUrl(qrSession.id)} size={280} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ wordBreak: 'break-all', textAlign: 'center', maxWidth: 400 }}
              >
                {rmetPageUrl(qrSession.id)}
              </Typography>
              {qrSession.passcode && (
                <Typography variant="h6" letterSpacing={4}>
                  {t('rmet.admin_passcode')}: {qrSession.passcode}
                </Typography>
              )}
            </>
          )}
        </Box>
        <DialogActions>
          <Button onClick={() => setQrSession(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <RmetSubmissionDialog
        submission={openSubmission}
        onClose={() => setOpenSubmission(null)}
        onRescore={handleRescore}
      />

      {/* Sessions panel */}
      <Paper sx={{ mb: 2 }}>
        <Grid
          spacing={2}
          padding={3}
          container
          direction="column"
          sx={{ alignItems: 'flex-start' }}
        >
          <Box display="flex" alignItems="center" gap={2} width="100%">
            <Typography variant="h6" sx={{ flex: 1 }}>
              {t('rmet.admin_title')}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={loading}
              onClick={() => setCreateOpen(true)}
            >
              {t('rmet.admin_create')}
            </Button>
          </Box>

          {sessions.length === 0 && !loading && (
            <Typography variant="body2" color="text.secondary">
              {t('rmet.admin_empty')}
            </Typography>
          )}

          {sessions.map((s) => {
            const isOpen = expanded.has(s.id);
            const rows = submissions[s.id];
            const isLoading = loadingSubmissions.has(s.id);
            return (
              <Box key={s.id} width="100%">
                <Box display="flex" alignItems="center" gap={1} width="100%" flexWrap="wrap">
                  <Tooltip title={t('rmet.admin_submissions')}>
                    <IconButton size="small" onClick={() => toggleExpanded(s.id)}>
                      {isOpen ? (
                        <KeyboardArrowDownIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowRightIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Chip
                    size="small"
                    label={
                      s.status === 'open'
                        ? t('rmet.admin_status_open')
                        : t('rmet.admin_status_closed')
                    }
                    color={s.status === 'open' ? 'success' : 'default'}
                  />
                  <Chip size="small" label={s.lang?.toUpperCase()} variant="outlined" />
                  <Typography
                    variant="body2"
                    sx={{ flex: 1, cursor: 'pointer' }}
                    onClick={() => toggleExpanded(s.id)}
                  >
                    {s.label}
                  </Typography>
                  {rows && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t('rmet.admin_submission_count', { count: rows.length })}
                    />
                  )}
                  {isAdmin && s.createdBy && (
                    <Typography variant="body2" color="text.secondary">
                      {s.createdBy}
                    </Typography>
                  )}
                  {s.status === 'open' && (
                    <>
                      <Tooltip title={t('rmet.admin_copy_link')}>
                        <IconButton
                          size="small"
                          onClick={() => navigator.clipboard.writeText(rmetPageUrl(s.id))}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('rmet.admin_qr')}>
                        <IconButton size="small" onClick={() => setQrSession(s)}>
                          <QrCodeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('rmet.admin_close')}>
                        <IconButton size="small" onClick={() => handleClose(s.id)}>
                          <LockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title={t('common.remove')}>
                    <IconButton size="small" onClick={() => handleDelete(s.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Collapse in={isOpen} unmountOnExit>
                  <Box pl={5} py={1}>
                    {isLoading && <CircularProgress size={20} />}
                    {!isLoading && rows?.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {t('rmet.admin_no_submissions')}
                      </Typography>
                    )}
                    {!isLoading &&
                      rows?.map((r) => {
                        const status = r.promptScoreStatus ?? 'pending';
                        return (
                          <Box
                            key={r.id}
                            display="flex"
                            alignItems="center"
                            gap={1}
                            flexWrap="wrap"
                            onClick={() => setOpenSubmission(r)}
                            sx={{
                              py: 0.75,
                              px: 1,
                              borderRadius: 1,
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <Typography variant="body2" sx={{ minWidth: 160 }}>
                              {new Date(r.submittedAt).toLocaleString()}
                            </Typography>
                            <Chip size="small" label={`RMET ${r.score} / ${RMET_TOTAL}`} />
                            <Chip
                              size="small"
                              color={status === 'done' ? 'primary' : 'default'}
                              variant={status === 'done' ? 'filled' : 'outlined'}
                              label={
                                r.promptScores
                                  ? promptScoreLabel(r.promptScores, t)
                                  : t(`rmet.detail_status_${status}`)
                              }
                            />
                            <Typography variant="caption" color="text.secondary">
                              {durationBetween(
                                r.timings?.rmetStartedAt,
                                r.timings?.promptsEndedAt
                              ) ?? '—'}
                            </Typography>
                          </Box>
                        );
                      })}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Grid>
      </Paper>
    </>
  );
};
